import { useCallback, useEffect, useRef, useState } from 'react'
import { normalizeTenantId } from './tenantScope'
import { subscribeAuthEvents } from '@/shared/auth/authEvents'

type ParseSseResult<TSsePayload> = { ok: true; value: TSsePayload } | { ok: false }

export interface UseTenantHybridStreamCoreOptions<TSnapshot, TSsePayload> {
  tenantId?: number | string
  ssePath: string
  fetchSnapshot: (tenantId?: number) => Promise<TSnapshot>
  onHttpSnapshot: (snapshot: TSnapshot) => void
  onSseSnapshot: (payload: TSsePayload) => void
  parseSseSnapshot: (raw: unknown) => ParseSseResult<TSsePayload>
}

export interface UseTenantHybridStreamCoreResult {
  loaded: boolean
  connected: boolean
  error: string | null
  nudge: () => void
}

export function useTenantHybridStreamCore<TSnapshot, TSsePayload>(
  options: UseTenantHybridStreamCoreOptions<TSnapshot, TSsePayload>,
): UseTenantHybridStreamCoreResult {
  const {
    tenantId,
    ssePath,
    fetchSnapshot,
    onHttpSnapshot,
    onSseSnapshot,
    parseSseSnapshot,
  } = options

  const [loaded, setLoaded] = useState(false)
  const [connected, setConnected] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [connectKey, setConnectKey] = useState(0)
  const esRef = useRef<EventSource | null>(null)
  const mountedRef = useRef(true)
  const streamPausedRef = useRef(false)
  const consecutiveSseErrorsRef = useRef(0)
  const tenantEpochRef = useRef(0)
  const fetchSnapshotRef = useRef(fetchSnapshot)
  const onHttpSnapshotRef = useRef(onHttpSnapshot)
  const onSseSnapshotRef = useRef(onSseSnapshot)
  const parseSseSnapshotRef = useRef(parseSseSnapshot)
  const lastHttpSyncAtRef = useRef(0)
  const lastConnectSyncAtRef = useRef(0)
  const normalizedTenantId = normalizeTenantId(tenantId)

  fetchSnapshotRef.current = fetchSnapshot
  onHttpSnapshotRef.current = onHttpSnapshot
  onSseSnapshotRef.current = onSseSnapshot
  parseSseSnapshotRef.current = parseSseSnapshot

  const fetchHttp = useCallback(async (opts?: { reconnectAfter?: boolean; minIntervalMs?: number }) => {
    const reconnectAfter = opts?.reconnectAfter === true
    const minIntervalMs = Number(opts?.minIntervalMs ?? 0)
    const now = Date.now()
    if (minIntervalMs > 0 && now - lastHttpSyncAtRef.current < minIntervalMs) return
    lastHttpSyncAtRef.current = now

    const fetchEpoch = tenantEpochRef.current
    try {
      const next = await fetchSnapshotRef.current(normalizedTenantId)
      if (!mountedRef.current) return
      if (fetchEpoch !== tenantEpochRef.current) return
      onHttpSnapshotRef.current(next)
      setLoaded(true)
      setError(null)
      if (reconnectAfter) {
        try { esRef.current?.close() } catch {}
        esRef.current = null
        setConnectKey(k => k + 1)
      }
    } catch (e: any) {
      if (!mountedRef.current) return
      setError(e?.message || 'Snapshot load failed')
      setLoaded(true)
    }
  }, [normalizedTenantId])

  const nudge = useCallback(() => {
    try { esRef.current?.close() } catch {}
    esRef.current = null
    void fetchHttp({ reconnectAfter: true })
  }, [fetchHttp])

  useEffect(() => {
    mountedRef.current = true
    return () => { mountedRef.current = false }
  }, [])

  useEffect(() => {
    tenantEpochRef.current += 1
    streamPausedRef.current = false
    consecutiveSseErrorsRef.current = 0
    void fetchHttp({ reconnectAfter: false })
  }, [fetchHttp, tenantId])

  // Post-login bootstrap: when auth flips from visitor/expired to a real
  // session in the same tab, force an immediate refetch + short retry burst
  // so floor materialization converges without manual hard-refresh.
  useEffect(() => {
    let cancelled = false
    const timers = new Set<number>()

    const schedule = (delayMs: number) => {
      const id = window.setTimeout(() => {
        timers.delete(id)
        if (cancelled) return
        void fetchHttp({ reconnectAfter: true })
      }, delayMs)
      timers.add(id)
    }

    const unsub = subscribeAuthEvents((evt) => {
      if (evt.type === 'logout') {
        streamPausedRef.current = true
        consecutiveSseErrorsRef.current = 0
        setConnected(false)
        setError('Auth session changed')
        try { esRef.current?.close() } catch {}
        esRef.current = null
        return
      }
      if (evt.type === 'login') {
        streamPausedRef.current = false
        consecutiveSseErrorsRef.current = 0
        void fetchHttp({ reconnectAfter: true })
        schedule(900)
        schedule(2200)
        schedule(4200)
      }
    })

    return () => {
      cancelled = true
      timers.forEach((id) => window.clearTimeout(id))
      timers.clear()
      unsub()
    }
  }, [fetchHttp])

  // Edge-heal: even with SSE, clients can miss frames during reconnect windows.
  // Keep eventual convergence without requiring a manual page refresh.
  useEffect(() => {
    if (typeof window === 'undefined') return

    const onFocus = () => {
      void fetchHttp({ reconnectAfter: false, minIntervalMs: 3_000 })
    }
    const onVisibility = () => {
      if (document.visibilityState === 'visible') {
        void fetchHttp({ reconnectAfter: false, minIntervalMs: 3_000 })
      }
    }

    window.addEventListener('focus', onFocus)
    document.addEventListener('visibilitychange', onVisibility)

    return () => {
      window.removeEventListener('focus', onFocus)
      document.removeEventListener('visibilitychange', onVisibility)
    }
  }, [fetchHttp])

  // Background revalidate cadence:
  // - fast when SSE is down to recover quickly
  // - slower when connected to heal missed frames with low overhead
  useEffect(() => {
    const intervalMs = connected ? 25_000 : 7_000
    const id = setInterval(() => {
      void fetchHttp({ reconnectAfter: false, minIntervalMs: 2_000 })
    }, intervalMs)
    return () => clearInterval(id)
  }, [connected, fetchHttp])

  useEffect(() => {
    if (typeof EventSource === 'undefined') return
    if (streamPausedRef.current) return
    const sseEpoch = tenantEpochRef.current
    const es = new EventSource(ssePath)
    esRef.current = es

    es.addEventListener('hello', () => {
      consecutiveSseErrorsRef.current = 0
      setConnected(true)
      setError(null)
      const now = Date.now()
      if (now - lastConnectSyncAtRef.current > 3_000) {
        lastConnectSyncAtRef.current = now
        void fetchHttp({ reconnectAfter: false, minIntervalMs: 2_000 })
      }
    })

    es.addEventListener('snapshot', (ev: MessageEvent) => {
      if (sseEpoch !== tenantEpochRef.current) return
      try {
        const raw = JSON.parse(ev.data)
        const parsed = parseSseSnapshotRef.current(raw)
        if (!parsed.ok) return
        consecutiveSseErrorsRef.current = 0
        onSseSnapshotRef.current(parsed.value)
        setLoaded(true)
        setConnected(true)
        setError(null)
      } catch {
        // Ignore malformed frames and keep stream alive.
      }
    })

    es.addEventListener('error', (ev: Event) => {
      try {
        const mev = ev as MessageEvent
        if (mev.data) {
          const payload = JSON.parse(mev.data)
          setError(payload?.error || 'Stream error')
        }
      } catch {}
    })

    let sseAttempt = 0
    es.onerror = () => {
      setConnected(false)
      consecutiveSseErrorsRef.current += 1
      try { es.close() } catch {}
      esRef.current = null

      if (streamPausedRef.current) return

      if (consecutiveSseErrorsRef.current >= 3) {
        // Force an HTTP heal after repeated stream failures (commonly auth
        // propagation/reconnect edge cases) so UI state converges quickly.
        void fetchHttp({ reconnectAfter: false, minIntervalMs: 0 })
      }

      const base = 1000
      const max = 30_000
      const jitter = 1500
      const delay = Math.min(base * (1 << Math.min(sseAttempt, 10)), max) + Math.floor(Math.random() * jitter)
      sseAttempt += 1
      setTimeout(() => {
        if (streamPausedRef.current) return
        setConnectKey(k => k + 1)
      }, delay)
    }

    es.onopen = () => {
      consecutiveSseErrorsRef.current = 0
      setConnected(true)
      setError(null)
      const now = Date.now()
      if (now - lastConnectSyncAtRef.current > 3_000) {
        lastConnectSyncAtRef.current = now
        void fetchHttp({ reconnectAfter: false, minIntervalMs: 2_000 })
      }
    }

    return () => {
      try { es.close() } catch {}
      esRef.current = null
    }
  }, [connectKey, ssePath, tenantId, fetchHttp])

  return { loaded, connected, error, nudge }
}
